import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProjectStore } from "@/stores/projects";

export function ProjectsList() {
  const { projects, loading, fetchProjects, createProject, deleteProject } =
    useProjectStore();
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const project = await createProject(name.trim(), description.trim() || undefined);
    setName("");
    setDescription("");
    setShowCreate(false);
    navigate(`/projects/${project.id}`);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
        <h1 className="text-lg font-semibold text-zinc-100">Projects</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500"
        >
          + New Project
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {showCreate && (
          <form
            onSubmit={handleCreate}
            className="mb-6 rounded-xl border border-zinc-700 bg-zinc-900 p-4"
          >
            <div className="mb-3">
              <label className="mb-1 block text-sm text-zinc-400">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-blue-500"
                placeholder="My Project"
                autoFocus
                required
              />
            </div>
            <div className="mb-3">
              <label className="mb-1 block text-sm text-zinc-400">
                Description (optional)
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-blue-500"
                placeholder="What's this project about?"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500"
              >
                Create
              </button>
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="rounded-lg bg-zinc-800 px-4 py-2 text-sm text-zinc-400 hover:bg-zinc-700"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12 text-sm text-zinc-500">
            Loading...
          </div>
        ) : projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-zinc-500">
            <div className="text-4xl">📁</div>
            <p className="text-sm">No projects yet</p>
            <button
              onClick={() => setShowCreate(true)}
              className="text-sm text-blue-400 hover:text-blue-300"
            >
              Create your first project
            </button>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((project) => (
              <div
                key={project.id}
                className="group cursor-pointer rounded-xl border border-zinc-800 bg-zinc-900/50 p-4 transition-colors hover:border-zinc-700 hover:bg-zinc-900"
                onClick={() => navigate(`/projects/${project.id}`)}
              >
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-medium text-zinc-200">{project.name}</h3>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm("Delete this project?")) {
                        deleteProject(project.id);
                      }
                    }}
                    className="text-zinc-600 opacity-0 transition-opacity hover:text-red-400 group-hover:opacity-100"
                    title="Delete"
                  >
                    ✕
                  </button>
                </div>
                {project.description && (
                  <p className="mb-2 text-xs text-zinc-500 line-clamp-2">
                    {project.description}
                  </p>
                )}
                <div className="flex items-center gap-2 text-[10px] text-zinc-600">
                  <span>
                    {project.sessionKey ? "● Active" : "○ No session"}
                  </span>
                  <span>·</span>
                  <span>
                    {new Date(project.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
